import React, { useEffect, useState } from 'react';
import { TrendingDown, Loader2, AlertTriangle, Users, Clock, Zap, ArrowDown, ArrowUp, Minus } from 'lucide-react';
import { adminDb } from '../lib/adminDb';

interface UserChurn {
  id: string;
  email: string;
  displayName: string;
  riskScore: number;
  riskLevel: 'high' | 'medium' | 'low';
  lastActive: string;
  questionsAnswered: number;
  streak: number;
  isPremium: boolean;
  recommendation: string;
}

const RISK_CONFIG = {
  high: { label: 'Hoch', color: 'var(--danger)', badge: 'badge-danger', icon: <ArrowDown size={12} /> },
  medium: { label: 'Mittel', color: 'var(--warning)', badge: 'badge-warning', icon: <Minus size={12} /> },
  low: { label: 'Niedrig', color: 'var(--success)', badge: 'badge-success', icon: <ArrowUp size={12} /> },
};

export const ChurnPredictionPage: React.FC = () => {
  const [users, setUsers] = useState<UserChurn[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterRisk, setFilterRisk] = useState('');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    const { data } = await adminDb.from('users').select('id, email, data, updated_at');
    const now = Date.now();

    const scored: UserChurn[] = (data || []).map(u => {
      const lastActive = u.updated_at || u.data?.lastLogin || new Date().toISOString();
      const daysSinceActive = Math.floor((now - new Date(lastActive).getTime()) / (1000 * 60 * 60 * 24));
      const questionsAnswered = u.data?.completedQuestions?.length || 0;
      const streak = u.data?.streak?.current || 0;
      const isPremium = u.data?.isPremium || false;
      const exams = u.data?.examHistory?.length || 0;

      // Churn-Score: 0 (sicher) – 100 (wird churnen)
      let score = 0;
      if (daysSinceActive > 14) score += 35;
      else if (daysSinceActive > 7) score += 20;
      else if (daysSinceActive > 3) score += 10;
      if (questionsAnswered < 10) score += 20;
      else if (questionsAnswered < 50) score += 10;
      if (streak === 0) score += 15;
      if (exams === 0) score += 15;
      if (!isPremium) score += 5;
      score = Math.min(score, 100);

      const riskLevel: 'high' | 'medium' | 'low' = score >= 60 ? 'high' : score >= 30 ? 'medium' : 'low';

      let recommendation = '';
      if (daysSinceActive > 14) recommendation = 'Push-Nachricht: "Wir vermissen dich!" senden';
      else if (questionsAnswered < 10) recommendation = 'Onboarding-Erinnerung senden';
      else if (streak === 0) recommendation = 'Streak-Challenge vorschlagen';
      else if (exams === 0) recommendation = 'Erste Pruefungssimulation empfehlen';
      else if (!isPremium && score >= 30) recommendation = 'Premium-Rabatt anbieten';
      else recommendation = 'Weiter beobachten';

      return {
        id: u.id, email: u.email || 'Unbekannt',
        displayName: u.data?.displayName || u.email?.split('@')[0] || 'Nutzer',
        riskScore: score, riskLevel, lastActive,
        questionsAnswered, streak, isPremium, recommendation,
      };
    }).sort((a, b) => b.riskScore - a.riskScore);

    setUsers(scored);
    setLoading(false);
  };

  const filtered = filterRisk ? users.filter(u => u.riskLevel === filterRisk) : users;
  const highRisk = users.filter(u => u.riskLevel === 'high').length;
  const mediumRisk = users.filter(u => u.riskLevel === 'medium').length;
  const lowRisk = users.filter(u => u.riskLevel === 'low').length;
  const avgScore = users.length > 0 ? Math.round(users.reduce((s, u) => s + u.riskScore, 0) / users.length) : 0;

  if (loading) {
    return (
      <div>
        <div className="page-header"><h1><TrendingDown size={24} /> Churn-Prediction</h1></div>
        <div style={{ textAlign: 'center', padding: 60 }}><Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} /></div>
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header"><h1><TrendingDown size={24} /> Churn-Prediction</h1></div>

      <div className="grid-cols-4 mb-6">
        <div className="kpi-card"><span className="kpi-label">Ø Churn-Score</span><span className="kpi-value" style={{ color: avgScore >= 50 ? 'var(--danger)' : avgScore >= 25 ? 'var(--warning)' : 'var(--success)' }}>{avgScore}</span></div>
        <div className="kpi-card"><span className="kpi-label">🔴 Hohes Risiko</span><span className="kpi-value" style={{ color: 'var(--danger)' }}>{highRisk}</span></div>
        <div className="kpi-card"><span className="kpi-label">🟡 Mittleres Risiko</span><span className="kpi-value" style={{ color: 'var(--warning)' }}>{mediumRisk}</span></div>
        <div className="kpi-card"><span className="kpi-label">🟢 Niedriges Risiko</span><span className="kpi-value" style={{ color: 'var(--success)' }}>{lowRisk}</span></div>
      </div>

      <div className="card mb-6" style={{ padding: 16, background: 'var(--bg-elevated)' }}>
        <p className="text-xs text-muted" style={{ marginBottom: 8 }}><strong>Scoring-Algorithmus:</strong></p>
        <div className="flex gap-4 flex-wrap text-xs text-muted">
          <span>+35 Punkte: &gt;14 Tage inaktiv</span>
          <span>+20 Punkte: &lt;10 Fragen beantwortet</span>
          <span>+15 Punkte: Kein Streak</span>
          <span>+15 Punkte: Keine Simulation</span>
          <span>+5 Punkte: Kein Premium</span>
        </div>
      </div>

      <div className="table-container">
        <div className="table-toolbar">
          <select className="input" style={{ width: 160 }} value={filterRisk} onChange={e => setFilterRisk(e.target.value)}>
            <option value="">Alle Risiken</option>
            <option value="high">🔴 Hohes Risiko</option>
            <option value="medium">🟡 Mittleres Risiko</option>
            <option value="low">🟢 Niedriges Risiko</option>
          </select>
          <span className="text-xs text-muted">{filtered.length} Nutzer</span>
        </div>

        <table>
          <thead><tr><th>Nutzer</th><th>Score</th><th>Risiko</th><th>Letzte Aktivitaet</th><th>Fragen</th><th>Streak</th><th>Empfehlung</th></tr></thead>
          <tbody>
            {filtered.slice(0, 50).map(u => {
              const rc = RISK_CONFIG[u.riskLevel];
              return (
                <tr key={u.id}>
                  <td>
                    <div><span style={{ fontWeight: 600, fontSize: 13 }}>{u.displayName}</span></div>
                    <div className="text-xs text-muted">{u.email}</div>
                  </td>
                  <td>
                    <div className="flex items-center gap-2">
                      <div style={{ width: 40, height: 6, background: 'var(--border-light)', borderRadius: 3, overflow: 'hidden' }}>
                        <div style={{ width: `${u.riskScore}%`, height: '100%', background: rc.color, borderRadius: 3 }} />
                      </div>
                      <span className="text-sm" style={{ fontWeight: 700 }}>{u.riskScore}</span>
                    </div>
                  </td>
                  <td><span className={`badge ${rc.badge}`}>{rc.icon} {rc.label}</span></td>
                  <td className="text-xs text-muted">{new Date(u.lastActive).toLocaleDateString('de-DE')}</td>
                  <td className="text-sm">{u.questionsAnswered}</td>
                  <td className="text-sm">{u.streak > 0 ? `${u.streak} 🔥` : '—'}</td>
                  <td className="text-xs" style={{ maxWidth: 200 }}>{u.recommendation}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};
